import React from 'react'
import styled from 'styled-components'

import { defaultCTAURI } from 'src/modules/utils/constants'

import { createUrl } from 'src/modules/utils/createUrl'
import { useSelector } from 'react-redux'
import { selectCTAParams } from 'src/modules/reducers/params'
import CTAButton from 'src/components/common/CTAButtonNew'
import { replaceUrlParam } from 'src/modules/utils/replaceUrlParam'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { selectStateForProtobuf } from 'src/modules/reducers'
import { useCtaText } from 'src/modules/hooks/useCtaText'
import colors from 'src/constants/marketing-ds/colors'
import { heading, typographyMixin, body } from 'src/constants/marketing-ds/typography'
import { spacing } from 'src/constants/marketing-ds/spacing'
import MatchTile from './MatchTile'

const breakpoint = 981

const MultiPPVSection = ({
  ctaOfferCode,
  ctaText,
  image,
  title,
  subtitle,
  miceText,
  background,
  lang = 'en',
  ctaHrefOverride,
  showHeadlineTop = false,
  disableFreeTrial,
  market,
  urlParams,
  ppvEvents,
  multiPPVTitle,
  multiPPVBody,
}) => {
  const parsedParams = useSelector(selectCTAParams)
  const stateForProtobuf = useSelector(selectStateForProtobuf)

  let ctaURL = createUrl(defaultCTAURI, parsedParams)
  if (urlParams) {
    urlParams.forEach((param) => {
      const key = Object.keys(param)[0]
      const value = param[key]
      ctaURL = replaceUrlParam(ctaURL, key, value)
    })
  }
  ctaURL = replaceUrlParam(ctaURL, 'comp', 'feature')

  const ctaCopy = useCtaText({ lang, market, disableFreeTrial, ctaText })
  const handleOnClick = (e) => {
    fireUIInteractionEvent(
      {
        element: ctaCopy,
        component: 'multi_ppv_section_cta',
        type: 'click',
      },
      stateForProtobuf
    )
  }

  return (
    <SectionWrapper background={background} className="highlight-section" id="matches">
      <SectionInner className="highlight-section-inner">
        <SectionContent className="highlight-section-content">
          <SectionTitle>
            {multiPPVTitle || (
              <>
                Buy all matches and <div className="desktop-line-break"> save a bundle</div>
              </>
            )}
          </SectionTitle>
          {multiPPVBody && <SectionParagraph>{multiPPVBody}</SectionParagraph>}
          {ctaOfferCode && (
            <SectionCTA>
              <ButtonWrapper>
                <CTAButton
                  onClick={handleOnClick}
                  link={replaceUrlParam(ctaURL, 'offer', ctaOfferCode)}
                >
                  {ctaCopy}
                </CTAButton>
              </ButtonWrapper>
              {miceText && <div className="mice-text">{miceText}</div>}
            </SectionCTA>
          )}
        </SectionContent>
        <MatchesWrapper className="highlight-section-image">
          {ppvEvents.map((match) => {
            const purchasable = match.purchasable ?? true
            return (
              <div className="tile-wrapper" key={match.id}>
                <MatchTile
                  item={match}
                  link={replaceUrlParam(ctaURL, 'offer', match.ratePlanCode)}
                  purchasable={purchasable}
                />
                {purchasable ? (
                  <a
                    href={replaceUrlParam(ctaURL, 'offer', match.ratePlanCode)}
                    style={{
                      textAlign: 'center',
                    }}
                  >
                    <MatchTitleText isAllMatches={match.isAllMatches}>
                      {match.title} ${match.price}
                    </MatchTitleText>{' '}
                  </a>
                ) : (
                  <span style={{ textAlign: 'center', color: colors.white }}>
                    <MatchTitleText isAllMatches={match.isAllMatches}>
                      {match.title} ${match.price}
                    </MatchTitleText>{' '}
                  </span>
                )}
              </div>
            )
          })}
        </MatchesWrapper>
      </SectionInner>
    </SectionWrapper>
  )
}

export default MultiPPVSection

const cssBreakpoint = 500
const desktopBreak = cssBreakpoint + 'px'
const mobileBreak = cssBreakpoint - 1 + 'px'

const SectionWrapper = styled.section`
  width: 100%;
  padding: 90px 0;
  background: ${colors.gradients.midnight.light};

  @media (max-width: 981px) {
    padding: 30px 0;
  }

  .mice-text {
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    max-width: 800px;
    margin: 0 auto;
    text-align: center;

    @media (min-width: 982px) {
      text-align: left;
      margin-top: 20px;
    }
  }
`

const SectionInner = styled.div`
  display: flex;
  width: 100%;
  max-width: 1150px;
  margin: 0 auto;
  align-items: center;
  padding: 0px ${spacing.lg};
  gap: 0 ${spacing.lg};
  justify-content: space-between;
  box-sizing: border-box;

  @media (max-width: 981px) {
    flex-direction: column;
    padding: 10px 0 20px 0;
  }
`

const MatchesWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${spacing.xxl} ${spacing.xl};

  @media (min-width: 982px) {
    flex: 1 1 auto;
  }

  @media (max-width: 1100px) {
    ${'' /* gap: ${spacing.xl} ${spacing.lg}; */}
  }

  @media (max-width: 981px) {
    margin: 10px auto 30px auto;
  }

  @media (max-width: ${mobileBreak}) {
    grid-template-columns: repeat(1, 1fr);

    gap: ${spacing.lg};
  }

  .carousel-item-thumbnail {
    width: 100%;
    position: relative;
    overflow: hidden;
  }

  .tile-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    align-self: flex-start;
    a {
      ${typographyMixin(heading.xs)}
      text-decoration-line: underline;
      text-decoration-style: solid;
      text-decoration-skip-ink: auto;
      text-decoration-thickness: auto;
      text-underline-offset: auto;
      text-underline-position: from-font;
    }
  }
`

const SectionContent = styled.div`
  color: #fff;

  @media (min-width: 982px) {
    flex: 1 1 50%;
    ${'' /* min-width: 505px; */}
  }

  @media (max-width: 981px) {
    text-align: center;
    width: 80%;
    margin-left: auto;
    margin-right: auto;
  }
`

const SectionTitle = styled.h2`
  ${typographyMixin(heading.xl)}
  width: 100%;
  @media (max-width: 1100px) {
    ${typographyMixin(heading.lg)}
  }

  @media (max-width: 981px) {
    ${typographyMixin(heading.lg)}
    padding-bottom: 0;
    text-align: center;
  }

  @media (max-width: 736px) {
    ${typographyMixin(heading.md)}
  }
`
const SectionParagraph = styled.p`
  font-size: 20px;
  line-height: 1.6;
  margin-bottom: 30px;

  max-width: 460px;
  @media (max-width: 981px) {
    margin: 15px auto 30px;
  }
`

const SectionCTA = styled.div``

const ButtonWrapper = styled.div`
  position: relative;
  width: 100%;
  margin: 30px auto;
`

const MatchTitleText = styled.span`
  ${({ isAllMatches }) =>
    isAllMatches
      ? `${typographyMixin(heading.xs)}`
      : `${typographyMixin(body.xs)}
          text-decoration: none;`}
`
